import * as angular from 'angular';

import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import { ScimTokenResource } from '@mode/legacy/data-access-model-js';

import { OrganizationResource } from '@mode/legacy/data-access-model-js';
import { IdentityScimService } from './identity-scim.service';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { Observable } from 'rxjs';
import { getAppName } from '@mode/shared/util-js';

class IdentityScimController {
  readonly appName = getAppName();
  private organization: OrganizationResource;
  private scimEnabled: boolean;
  private scimToken: ScimTokenResource;
  private aadScimEnabled$: Observable<boolean>;

  static $inject = ['$scope', 'IdentityScimService', 'FeatureFlagsFacade'];

  constructor(
    private $scope: angular.IScope,
    private identityScimService: IdentityScimService,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    this.aadScimEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.AzureActiveDirectoryScim);
  }

  $onInit() {
    this.identityScimService
      .getScimToken(this.organization)
      .then((scimToken: ScimTokenResource) => {
        this.updateScimToken(scimToken);
      })
      .catch((err) => {
        console.error(err);
        BugsnagHelpers.notifySafely(err);
      });
  }

  handleScimToggle(isChecked): void {
    if (isChecked) {
      this.identityScimService
        .enableScim(this.organization, this.scimToken)
        .then((scimToken: ScimTokenResource) => this.updateScimToken(scimToken))
        .catch((err) => {
          console.error(err);
          BugsnagHelpers.notifySafely(err);
        });
    } else {
      this.identityScimService
        .disableScimToken(this.scimToken)
        .then((scimToken: ScimTokenResource) => this.updateScimToken(scimToken))
        .catch((err) => {
          console.error(err);
          BugsnagHelpers.notifySafely(err);
        });
    }
  }

  handleProvisionToggle() {
    this.identityScimService
      .toggleProvisioning(this.scimToken)
      .then((updatedScimToken: ScimTokenResource) => {
        this.updateScimToken(updatedScimToken);
      })
      .catch((err) => {
        console.error(err);
        BugsnagHelpers.notifySafely(err);
      });
  }

  updateScimToken(scimToken: ScimTokenResource): void {
    this.$scope.$applyAsync(() => {
      this.scimToken = scimToken;
      this.scimEnabled = this.identityScimService.scimIsEnabled(this.scimToken);
    });
  }

  scimToggleIsEnabled(): boolean {
    return !!this.scimEnabled;
  }

  displayScimForm(): boolean {
    return !!this.scimEnabled;
  }

  getScimBearerToken(): string {
    if (this.scimToken) {
      return this.scimToken.bearer_token;
    }
  }

  getScimBaseUrl(): string {
    if (this.scimToken) {
      return this.scimToken.base_url;
    }
  }

  getScimProvisionEnabled(): boolean {
    if (this.scimToken) {
      return this.scimToken.only_scim_provision;
    }
  }
}

export const IdentityScimComponent: angular.IComponentOptions = {
  controller: IdentityScimController,
  controllerAs: 'ctrl',
  bindings: {
    organization: '<',
  },
  template: `
    <panel-section>
      <div class="section-header">
        <div class="section-header-description">
          <h4>
            SCIM
          </h4>
          <p>
            SCIM integration enables an identity provider to provision and deprovision users and groups in {{ ctrl.appName }}. The identity provider can assign the Admin role in {{ ctrl.appName }}.
          </p>
          <p ng-if="(ctrl.aadScimEnabled$ | async:this)">
          {{ ctrl.appName }} is designed to work specifically with Okta and Azure Active Directory SCIM providers.  Integrating with other providers through SCIM may not work correctly.
          </p>
          <p ng-if="!(ctrl.aadScimEnabled$ | async:this)">
            {{ ctrl.appName }} is designed to work specifically with Okta SCIM.
          </p>
        </div>
        <div class="section-header-aside">
          <identity-scim-toggle
            checked="ctrl.scimToggleIsEnabled()"
            on-toggle="ctrl.handleScimToggle($checked)"
          />
        </div>
      </div>

      <div
        ng-if="ctrl.displayScimForm()"
      >
        <identity-scim-form
          bearer-token="ctrl.getScimBearerToken()"
          base-url="ctrl.getScimBaseUrl()"
          scim-provision-enabled="ctrl.getScimProvisionEnabled()"
          on-provision-toggled="ctrl.handleProvisionToggle()"
        />
      </div>
    </panel-section>
  `,
};
